import { useEffect, useState } from 'react'
import {
  Typography, Card, Tabs, Table, Button, Tag, Select, Space,
  Modal, message, Popconfirm, Badge,
} from 'antd'
import { CheckOutlined, CloseOutlined, UserOutlined, MailOutlined } from '@ant-design/icons'
import axiosInstance from '@/lib/axios'

const { Title } = Typography

interface PendingUser {
  id: number
  nome: string
  cognome: string
  username: string
  email: string
  created_at: string
}

interface ActiveUser {
  id: number
  nome: string
  cognome: string
  username: string
  email: string
  ruolo: string
  attivo: boolean
  stato?: string
  created_at: string
}

interface Role {
  id: number
  codice: string
  nome: string
}

const UsersPage = () => {
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([])
  const [approvedUsers, setApprovedUsers] = useState<ActiveUser[]>([])
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState<number | null>(null)
  const [approveModal, setApproveModal] = useState<{ open: boolean; user: PendingUser | null }>({ open: false, user: null })
  const [selectedRuoloId, setSelectedRuoloId] = useState<number | null>(null)
  const [approving, setApproving] = useState(false)

  const loadData = async () => {
    setLoading(true)
    try {
      const [usersRes, rolesRes] = await Promise.all([
        axiosInstance.get('/users/'),
        axiosInstance.get('/permissions/roles'),
      ])
      const all: ActiveUser[] = usersRes.data
      setPendingUsers(all.filter((u: any) => u.stato === 'pending') as any)
      setApprovedUsers(all.filter((u: any) => u.stato === 'approved' && !u.attivo))
      setActiveUsers(all.filter((u: any) => u.attivo === true))
      setRoles(rolesRes.data)
    } catch {
      message.error('Errore nel caricamento utenti')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadData() }, [])

  const handleApprove = async () => {
    if (!approveModal.user || !selectedRuoloId) return
    setApproving(true)
    try {
      await axiosInstance.post(`/auth/approve/${approveModal.user.id}`, { ruolo_id: selectedRuoloId })
      message.success('Utente approvato e notificato via email')
      setApproveModal({ open: false, user: null })
      setSelectedRuoloId(null)
      loadData()
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Errore durante l\'approvazione')
    } finally {
      setApproving(false)
    }
  }

  const handleResend = async (userId: number) => {
    setResending(userId)
    try {
      await axiosInstance.post(`/auth/resend-activation/${userId}`)
      message.success('Email di attivazione reinviata')
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Errore reinvio email')
    } finally {
      setResending(null)
    }
  }

  const handleReject = async (userId: number) => {
    try {
      await axiosInstance.post(`/auth/reject/${userId}`)
      message.success('Richiesta rifiutata')
      loadData()
    } catch {
      message.error('Errore durante il rifiuto')
    }
  }

  const pendingColumns = [
    { title: 'Nome', render: (_: any, r: PendingUser) => `${r.nome} ${r.cognome}` },
    { title: 'Username', dataIndex: 'username' },
    { title: 'Email', dataIndex: 'email' },
    {
      title: 'Richiesta il',
      dataIndex: 'created_at',
      render: (v: string) => new Date(v).toLocaleDateString('it-IT'),
    },
    {
      title: 'Azioni',
      render: (_: any, record: PendingUser) => (
        <Space>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            size="small"
            onClick={() => { setApproveModal({ open: true, user: record }); setSelectedRuoloId(null) }}
          >
            Approva
          </Button>
          <Popconfirm title="Rifiutare questa richiesta?" onConfirm={() => handleReject(record.id)} okText="Sì" cancelText="No">
            <Button danger icon={<CloseOutlined />} size="small">Rifiuta</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const approvedColumns = [
    { title: 'Nome', render: (_: any, r: ActiveUser) => `${r.nome} ${r.cognome}` },
    { title: 'Email', dataIndex: 'email' },
    { title: 'Ruolo', dataIndex: 'ruolo', render: (v: string) => <Tag color="blue">{v}</Tag> },
    {
      title: 'Stato',
      render: () => <Tag color="orange">In attesa attivazione</Tag>,
    },
    {
      title: 'Azioni',
      render: (_: any, record: ActiveUser) => (
        <Button
          size="small"
          icon={<MailOutlined />}
          loading={resending === record.id}
          onClick={() => handleResend(record.id)}
        >
          Reinvia link
        </Button>
      ),
    },
  ]

  const activeColumns = [
    { title: 'Nome', render: (_: any, r: ActiveUser) => `${r.nome} ${r.cognome}` },
    { title: 'Username', dataIndex: 'username' },
    { title: 'Email', dataIndex: 'email' },
    {
      title: 'Ruolo',
      dataIndex: 'ruolo',
      render: (v: string) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: 'Stato',
      dataIndex: 'attivo',
      render: (v: boolean) => v ? <Tag color="green">Attivo</Tag> : <Tag color="red">Disattivo</Tag>,
    },
  ]

  return (
    <div>
      <Title level={2}>
        <UserOutlined style={{ marginRight: 8 }} />
        Gestione Utenti
      </Title>

      <Tabs
        defaultActiveKey="pending"
        items={[
          {
            key: 'pending',
            label: (
              <Badge count={pendingUsers.length} offset={[8, 0]}>
                In attesa
              </Badge>
            ),
            children: (
              <Card>
                <Table
                  dataSource={pendingUsers}
                  columns={pendingColumns}
                  rowKey="id"
                  loading={loading}
                  locale={{ emptyText: 'Nessuna richiesta in attesa' }}
                />
              </Card>
            ),
          },
          {
            key: 'approved',
            label: (
              <Badge count={approvedUsers.length} offset={[8, 0]}>
                In attivazione
              </Badge>
            ),
            children: (
              <Card>
                <Table
                  dataSource={approvedUsers}
                  columns={approvedColumns}
                  rowKey="id"
                  loading={loading}
                  locale={{ emptyText: 'Nessun utente in attesa di attivazione' }}
                />
              </Card>
            ),
          },
          {
            key: 'active',
            label: 'Utenti attivi',
            children: (
              <Card>
                <Table
                  dataSource={activeUsers}
                  columns={activeColumns}
                  rowKey="id"
                  loading={loading}
                />
              </Card>
            ),
          },
        ]}
      />

      <Modal
        title={`Approva richiesta — ${approveModal.user?.nome} ${approveModal.user?.cognome}`}
        open={approveModal.open}
        onOk={handleApprove}
        onCancel={() => setApproveModal({ open: false, user: null })}
        okText="Approva e invia email"
        cancelText="Annulla"
        confirmLoading={approving}
        okButtonProps={{ disabled: !selectedRuoloId }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <p><b>Email:</b> {approveModal.user?.email}</p>
          <p>Seleziona il ruolo da assegnare:</p>
          <Select
            style={{ width: '100%' }}
            placeholder="Scegli un ruolo"
            value={selectedRuoloId}
            onChange={setSelectedRuoloId}
            options={roles.map(r => ({ value: r.id, label: r.codice }))}
          />
          <p style={{ color: '#888', fontSize: 12 }}>
            L'utente riceverà una email con un link per impostare la password e accedere all'applicazione.
          </p>
        </Space>
      </Modal>
    </div>
  )
}

export default UsersPage
